/**
 * Party addresses and bank accounts, inside the Compliance drawer.
 * @file src/web/features/admin/components/masters/PartyRecords.tsx
 *
 * Client sheet rows 43, 45, 49 and 53. Its own file rather than another 200
 * lines in ComplianceTab, which is already the longest drawer in Masters.
 *
 * Both lists load together from /parties/:ledgerId/records — one round trip
 * for two small tables that are always shown together.
 */

import { useCallback, useEffect, useState } from 'react';
import { Trash2, MapPin, Landmark, Star, Search, Loader2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetch, adminFetchJson } from '../../lib/admin-api';
import {
  Field, TextInput, Select, PrimaryBtn, GhostBtn, AddToggle, FormError,
  Loading, ErrBox, Pill, mutate, useDeleteConfirm,
} from './shared';

interface Address {
  id: string; kind: string;
  line1: string | null; line2: string | null; city: string | null;
  stateCode: string | null; stateName: string | null; pincode: string | null;
  countryCode: string | null; isDefault: boolean; source: string;
}
interface BankAccount {
  id: string; accountName: string | null; accountNumber: string; ifsc: string;
  bankName: string | null; branch: string | null; accountType: string | null; isPrimary: boolean;
}

const KIND_LABEL: Record<string, string> = {
  billing: 'Billing', shipping: 'Shipping', registered: 'Registered',
};
const ACCOUNT_TYPES = ['current', 'savings', 'od', 'cc'];

export function PartyRecords({ entityId, ledgerId, gstin, onCount }: {
  entityId: string | null; ledgerId: string; gstin?: string | null;
  onCount?: (n: { addresses: number; bank: number }) => void;
}) {
  const [addresses, setAddresses] = useState<Address[] | null>(null);
  const [bank, setBank] = useState<BankAccount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addingAddr, setAddingAddr] = useState(false);
  const [addingBank, setAddingBank] = useState(false);
  const { askDelete, deleteDialog } = useDeleteConfirm();

  const load = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<{ addresses: Address[]; bankAccounts: BankAccount[] }>(
      `/api/masters/parties/${ledgerId}/records`, { query: { entityId } },
    )
      .then((d) => {
        setAddresses(d.addresses); setBank(d.bankAccounts); setError(null);
        onCount?.({ addresses: d.addresses.length, bank: d.bankAccounts.length });
      })
      .catch((e: Error) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, ledgerId]);
  useEffect(load, [load]);

  const remove = async (path: string, what: string) => {
    try { await mutate(path, 'DELETE', { entityId }); toast.success(`${what} removed`); load(); }
    catch (e) { toast.error((e as Error).message); }
  };

  if (error) return <div className="mt-6"><ErrBox msg={error} /></div>;
  if (addresses === null) return <div className="mt-6"><Loading label="Loading addresses" /></div>;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Addresses</p>
        <AddToggle open={addingAddr} onToggle={() => setAddingAddr(!addingAddr)} label="Add address" />
      </div>

      {addingAddr && (
        <AddressForm
          entityId={entityId} ledgerId={ledgerId} gstin={gstin}
          onDone={() => { setAddingAddr(false); load(); }}
          onCancel={() => setAddingAddr(false)}
        />
      )}

      {addresses.length === 0 && !addingAddr ? (
        <p className="text-sm text-ink-500">
          None recorded. Billing and shipping are separate rows — the shipping state code is what decides
          place of supply on an invoice.
        </p>
      ) : (
        <ul className="space-y-2">
          {addresses.map((a) => (
            <li key={a.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-ink-200 px-4 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-ink-400" aria-hidden />
                  <Pill tone="neutral">{KIND_LABEL[a.kind] ?? a.kind}</Pill>
                  {a.isDefault && <Pill tone="brand">Default</Pill>}
                  {a.source === 'gstn' && <Pill tone="ok">From GSTN</Pill>}
                </div>
                <p className="mt-1 text-sm text-ink-700">
                  {[a.line1, a.line2, a.city, a.pincode].filter(Boolean).join(', ') || '—'}
                </p>
                <p className="mt-0.5 text-xs text-ink-500">
                  {a.stateCode
                    ? `State ${a.stateCode}${a.stateName ? ` · ${a.stateName}` : ''}`
                    : <span className="text-warn-600">no state code — place of supply cannot be derived</span>}
                  {a.countryCode && a.countryCode !== 'IN' ? ` · ${a.countryCode}` : ''}
                </p>
              </div>
              <GhostBtn tone="danger" onClick={() => askDelete(
                `the ${KIND_LABEL[a.kind]?.toLowerCase() ?? a.kind} address`,
                () => remove(`parties/${ledgerId}/addresses/${a.id}`, 'Address'),
              )}><Trash2 className="h-3 w-3" /></GhostBtn>
            </li>
          ))}
        </ul>
      )}

      <div className="mb-3 mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Bank accounts</p>
        <AddToggle open={addingBank} onToggle={() => setAddingBank(!addingBank)} label="Add account" />
      </div>
      <p className="mb-3 max-w-3xl text-[11px] text-ink-400">
        Where this party is paid. Separate from the client&rsquo;s own accounts under Entity → Bank details,
        which are for challans and reconciliation.
      </p>

      {addingBank && (
        <BankForm
          entityId={entityId} ledgerId={ledgerId}
          onDone={() => { setAddingBank(false); load(); }}
          onCancel={() => setAddingBank(false)}
        />
      )}

      {bank.length === 0 && !addingBank ? (
        <p className="text-sm text-ink-500">None recorded.</p>
      ) : (
        <ul className="space-y-2">
          {bank.map((b) => (
            <li key={b.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-ink-200 px-4 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Landmark className="h-3.5 w-3.5 shrink-0 text-ink-400" aria-hidden />
                  <span className="text-sm font-semibold text-ink-800">{b.bankName ?? 'Bank account'}</span>
                  {b.isPrimary && <Pill tone="brand"><Star className="h-3 w-3" />Primary</Pill>}
                </div>
                <p className="mt-1 font-mono text-sm text-ink-700">{b.accountNumber} · {b.ifsc}</p>
                <p className="mt-0.5 text-xs text-ink-500">
                  {[b.accountName, b.branch, b.accountType].filter(Boolean).join(' · ') || 'No further details'}
                </p>
              </div>
              <GhostBtn tone="danger" onClick={() => askDelete(
                `${b.bankName ?? 'the account'} ${b.accountNumber}`,
                () => remove(`parties/${ledgerId}/bank-accounts/${b.id}`, 'Account'),
              )}><Trash2 className="h-3 w-3" /></GhostBtn>
            </li>
          ))}
        </ul>
      )}
      {deleteDialog}
    </>
  );
}

/* ── forms ──────────────────────────────────────────────────────────────── */

function AddressForm({ entityId, ledgerId, gstin, onDone, onCancel }: {
  entityId: string | null; ledgerId: string; gstin?: string | null;
  onDone: () => void; onCancel: () => void;
}) {
  const [f, setF] = useState({
    kind: 'billing', line1: '', line2: '', city: '',
    stateCode: '', stateName: '', pincode: '', countryCode: '', isDefault: false,
    source: 'manual',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  /**
   * Sheet rows 45 and 49 ask for the address to be FETCHED from GSTN, not
   * typed. The lookup already returns it — the same call the Entity tab uses —
   * so this fills the form and marks the row `gstn`, which is what the "From
   * GSTN" pill on the list has always claimed.
   */
  const fillFromGstin = async () => {
    const g = (gstin ?? '').trim().toUpperCase();
    if (g.length !== 15) { toast.error('This party has no 15-character GSTIN on file'); return; }
    setLooking(true);
    try {
      const res = await adminFetch(`/api/gst/lookup/${g}`);
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.success) throw new Error(b?.error || 'Lookup failed');
      const a = b.data?.address ?? {};
      setF((prev) => ({
        ...prev,
        line1: [a.building, a.floor, a.street].filter(Boolean).join(', ') || prev.line1,
        line2: a.locality ?? prev.line2,
        city: a.city ?? a.district ?? prev.city,
        stateCode: a.stateCode ?? prev.stateCode,
        stateName: a.state ?? prev.stateName,
        pincode: a.pincode ?? prev.pincode,
        source: 'gstn',
      }));
      toast.success('Filled from the GST portal');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Lookup failed'); }
    finally { setLooking(false); }
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate(`parties/${ledgerId}/addresses`, 'POST', { entityId, ...f });
      toast.success('Address added'); onDone();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-ink-200 bg-white p-4">
      <FormError msg={err} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Kind">
          <Select value={f.kind} onChange={set('kind')}
            options={[{ value: 'billing', label: 'Billing' }, { value: 'shipping', label: 'Shipping' }, { value: 'registered', label: 'Registered' }]} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Address line 1"><TextInput value={f.line1} onChange={set('line1')} /></Field>
        </div>
        <Field label="Line 2"><TextInput value={f.line2} onChange={set('line2')} /></Field>
        <Field label="City"><TextInput value={f.city} onChange={set('city')} /></Field>
        <Field label="State code" hint="2-digit GST code — this is what a return carries.">
          <TextInput value={f.stateCode} onChange={set('stateCode')} placeholder="29" mono />
        </Field>
        <Field label="State name"><TextInput value={f.stateName} onChange={set('stateName')} /></Field>
        <Field label="Pincode"><TextInput value={f.pincode} onChange={set('pincode')} mono /></Field>
        <Field label="Country" hint="Leave blank for India.">
          <TextInput value={f.countryCode} onChange={(v) => set('countryCode')(v.toUpperCase())} placeholder="AE" mono />
        </Field>
        <Field label="Default for this kind">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.isDefault} onChange={(e) => setF((s) => ({ ...s, isDefault: e.target.checked }))} />
            Yes
          </label>
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <PrimaryBtn onClick={submit} busy={busy}>Add address</PrimaryBtn>
        <button
          onClick={fillFromGstin}
          disabled={looking || !gstin}
          title={gstin ? `Look up ${gstin}` : 'This party has no GSTIN on file'}
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50"
        >
          {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Fill from GSTIN
        </button>
        <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
      </div>
    </div>
  );
}

function BankForm({ entityId, ledgerId, onDone, onCancel }: {
  entityId: string | null; ledgerId: string; onDone: () => void; onCancel: () => void;
}) {
  const [f, setF] = useState({
    bankName: '', accountNumber: '', ifsc: '', accountName: '', branch: '',
    accountType: 'current', isPrimary: false,
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate(`parties/${ledgerId}/bank-accounts`, 'POST', { entityId, ...f });
      toast.success('Account added'); onDone();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-ink-200 bg-white p-4">
      <FormError msg={err} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Bank name"><TextInput value={f.bankName} onChange={set('bankName')} placeholder="HDFC Bank" /></Field>
        <Field label="Account number"><TextInput value={f.accountNumber} onChange={set('accountNumber')} mono /></Field>
        <Field label="IFSC" hint="11 characters — this is what routes the payment.">
          <TextInput value={f.ifsc} onChange={(v) => set('ifsc')(v.toUpperCase())} placeholder="HDFC0001234" mono />
        </Field>
        <Field label="Account name"><TextInput value={f.accountName} onChange={set('accountName')} /></Field>
        <Field label="Branch"><TextInput value={f.branch} onChange={set('branch')} /></Field>
        <Field label="Type"><Select value={f.accountType} onChange={set('accountType')} options={ACCOUNT_TYPES} /></Field>
        <Field label="Primary">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.isPrimary} onChange={(e) => setF((s) => ({ ...s, isPrimary: e.target.checked }))} />
            Yes
          </label>
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <PrimaryBtn onClick={submit} busy={busy}>Add account</PrimaryBtn>
        <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
      </div>
    </div>
  );
}
